import { useState, useMemo } from "react";
import { useSearchParams } from "react-router";
import { useTranslate } from "@refinedev/core";

import { usePaginatedTable, useQueryParams } from "@shared/hooks";
import { Notification } from "@modules/notifications/types";

export const NOTIFICATION_TABS = ["all", "email", "push", "errors"] as const;
export type NotificationTab = (typeof NOTIFICATION_TABS)[number];

const TAB_FILTERS: Record<NotificationTab, Record<string, string>> = {
  all: {},
  email: { notification_type: "email" },
  push: { notification_type: "push" },
  errors: { has_error: "true" },
};

export function useNotificationsList() {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const translate = useTranslate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getQueryParams } = useQueryParams();

  const tabParam = searchParams.get("tab") as NotificationTab | null;
  const tab: NotificationTab =
    tabParam && (NOTIFICATION_TABS as readonly string[]).includes(tabParam) ? tabParam : "all";

  const { tab: _tab, ...filters } = getQueryParams();

  const { rows, loading, refetch, ...tableProps } = usePaginatedTable<Notification>({
    resource: "v1/notifications_emails_admin",
    filters: { ...filters, ...TAB_FILTERS[tab] },
    sorters: { ordering: "-id" },
  });

  const setTab = (value: NotificationTab) => {
    setSelectedRows([]);
    setSearchParams(value === "all" ? {} : { tab: value });
  };

  const dataToExport = useMemo(
    () => (selectedRows.length > 0 ? rows.filter((row) => selectedRows.includes(row.id)) : rows),
    [selectedRows, rows],
  );

  return {
    tab,
    setTab,
    rows,
    loading,
    tableProps,
    selectedRows,
    dataToExport,
    refetch,
    setSelectedRows,
    translate,
  };
}
